import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { styled } from 'styled-components';
import { getSimilarTo } from '../../../resources/utils/getSimilarTo.js';
import { useQueryResourceContextSearch } from '../../../resources/queries/useQueryResourceContextSearch.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useContextAccessToken } from '../../../permission/contexts/AnonShareTokenContext.js';
import { ResourceSimpleGridList } from '../../../resources/components/ResourceList/ResourceSimpleList.js';
import { LockIcon } from '@fabric-app/icons/icons';
import { useExpandedResourceState, useShowExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { useQueryResourceList } from '../../../resources/queries/useQueryResourceList.js';

const MINIMUM_ITEMS_FOR_SIMILAR_ITEMS = 50;
const SpinnerContainer = styled.div.withConfig({
  displayName: "SimilarItemsList__SpinnerContainer",
  componentId: "sc-fc07a56e-0"
})([
  `width:100%;height:90%;display:flex;justify-content:center;align-items:center;`
]);
const SimilarItemsSidepanelContainer = styled.div.withConfig({
  displayName: "SimilarItemsList__SimilarItemsSidepanelContainer",
  componentId: "sc-fc07a56e-1"
})([
  `display:flex;justify-content:center;overflow:hidden;width:100%;color:black;`
]);
const NoSimilarItems = styled.div.withConfig({
  displayName: "SimilarItemsList__NoSimilarItems",
  componentId: "sc-fc07a56e-2"
})([
  `width:100%;height:100%;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;gap:35px;> svg{color:#00000040;}> p{max-width:270px;}padding-top:60px;color:rgba(255,255,255,0.5);text-align:center;font-size:13px;font-family:Inter,sans-serif;`
]);
const SimilarItemsList = ({ similarTo, selectedFdocId = "" }) => {
  const { expandedResourceSidebarTab } = useExpandedResourceState();
  const showExpandedResource = useShowExpandedResource();
  const acccessToken = useContextAccessToken();
  const { data, isLoading: isLoadingResourcesInfo } = useQueryResourceList({
    limit: 1
  });
  const total = data?.total ?? 0;
  const isDraft = typeof similarTo !== "string" && (similarTo.id === "draft" || similarTo.isDraft);
  const similarToParams = getSimilarTo(similarTo);
  const { resources, fetchNextPage, hasNextPage, isLoading } = useQueryResourceContextSearch({
    ...similarToParams,
    filters: {
      ...similarToParams.filters
    }
  }, {
    enabled: !isDraft && !isLoadingResourcesInfo && total > MINIMUM_ITEMS_FOR_SIMILAR_ITEMS
  });
  const filteredResources = useMemo(() => {
    if (selectedFdocId) {
      return resources.filter((item) => item.id !== selectedFdocId);
    }
    return resources;
  }, [
    resources,
    selectedFdocId
  ]);
  return isLoading || isLoadingResourcesInfo || isDraft ? /* @__PURE__ */ jsx(SpinnerContainer, {
    children: /* @__PURE__ */ jsx(Spinner, {
      thickness: 3,
      size: 24
    })
  }) : /* @__PURE__ */ jsx(SimilarItemsSidepanelContainer, {
    children: /* @__PURE__ */ jsx(ScrollArea.Default, {
      scrollbarVariant: "subtle",
      style: {
        width: "100%"
      },
      children: filteredResources.length > 0 && total > MINIMUM_ITEMS_FOR_SIMILAR_ITEMS ? /* @__PURE__ */ jsx(NewListGrid, {
        children: /* @__PURE__ */ jsx(ResourceSimpleGridList, {
          layoutId: "similar-items-list",
          onClickResource: (resource) => {
            showExpandedResource(resource.id, {
              tab: expandedResourceSidebarTab,
              openInNewTab: !!acccessToken,
              storeCurrentAsPrevious: true,
              disableSwitcher: true
            });
          },
          resources: filteredResources,
          fetchNextPage: hasNextPage ? fetchNextPage : void 0
        })
      }) : /* @__PURE__ */ jsx(NoSimilarItems, {
        children: total < MINIMUM_ITEMS_FOR_SIMILAR_ITEMS ? /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(LockIcon, {
              size: 58
            }),
            /* @__PURE__ */ jsx("p", {
              children: "Save more than 50 items to unlock AI suggestions"
            })
          ]
        }) : /* @__PURE__ */ jsx("p", {
          children: "No similar items found."
        })
      })
    })
  });
};
const NewListGrid = styled.div.withConfig({
  displayName: "SimilarItemsList__NewListGrid",
  componentId: "sc-fc07a56e-3"
})([
  `display:grid;grid-template-columns:1fr 1fr;padding:27px;gap:21px;`,
  `{padding:21px;}`
], mediaMobile);

export { SimilarItemsList as default };
//# sourceMappingURL=SimilarItemsList.js.map
